import { AppSpinner } from '@components/app-spinner';
import { RouteComponentProps } from '@reach/router';
import { ComponentType, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import useQueryParam from 'src/hooks/useQueryParam';
import { session } from 'src/modules/session';
import { getPageViewTM } from 'src/modules/tag-manager-helpers';
import { useAuthenticationStore } from 'src/stores/Authentication';

type ProtectedRouteProps = RouteComponentProps & {
  component: ComponentType<any>;
  gtmPageType?: string;
};

/**
 * Route guard. It decides during render but **navigates in effects**: a `window.location.replace`
 * called from a render body raced the effect that stored the incoming token, and which of the two
 * won came down to whether the browser unloaded the document before React flushed passive effects.
 * See docs/session-auth-refactor.md.
 *
 * Every navigation here goes through the session module, so the four call sites that could each
 * decide to leave for the identity provider now share one in-flight guard.
 */
export const ProtectedRoute = ({ component: Component, gtmPageType, ...props }: ProtectedRouteProps) => {
  const { queryParam: targetQueryParam } = useQueryParam('Target');
  const {
    i18n: { language },
  } = useTranslation();

  const {
    state: { hasAccess, canUseScout },
  } = useAuthenticationStore();

  const needsAuthentication = !hasAccess;
  // Logged in, but this dealer may not use Scout -- the backend issues a valid token either way.
  const isForbidden = hasAccess && !canUseScout;

  useEffect(() => {
    if (gtmPageType) {
      TagManager.dataLayer(getPageViewTM(language, gtmPageType));
    }
  }, [gtmPageType, language]);

  // Declared before the re-authentication effect on purpose: effects run in order, and a GRP entry
  // carrying a signed target must win over the generic bounce to the provider.
  useEffect(() => {
    if (targetQueryParam) session.beginTargetedAuthentication(targetQueryParam);
  }, [targetQueryParam]);

  useEffect(() => {
    if (needsAuthentication) session.beginReauthentication();
  }, [needsAuthentication]);

  useEffect(() => {
    if (isForbidden) session.goToForbidden();
  }, [isForbidden]);

  // A full-page navigation is in flight in all three cases. The spinner is themed (it renders
  // inside CustomerModeThemeWrapper) and matches the app's own Suspense fallback.
  if (targetQueryParam || needsAuthentication || isForbidden) return <AppSpinner />;

  return <Component {...props} />;
};
